import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Skeleton } from '../ui/skeleton';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { frontOfficeApi, roomsApi, reservationsApi } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { format } from 'date-fns';
import { 
  LogIn, LogOut, Users, BedDouble, Clock, AlertTriangle,
  User, Phone, Mail, CalendarDays, CheckCircle, XCircle,
  DoorOpen, DoorClosed, Sparkles, Wrench, Coffee
} from 'lucide-react';
import { cn } from '../../lib/utils';

export default function FrontDeskPage() {
  const { currentProperty } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('arrivals');
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [checkOutDialogOpen, setCheckOutDialogOpen] = useState(false);
  const [availableRooms, setAvailableRooms] = useState([]);

  const fetchDashboard = useCallback(async () => {
    if (!currentProperty) return;
    try {
      setLoading(true);
      const response = await frontOfficeApi.getDashboard(currentProperty.id);
      setDashboard(response.data);
    } catch (error) {
      toast.error('Failed to load front desk dashboard');
    } finally {
      setLoading(false);
    }
  }, [currentProperty]);

  useEffect(() => {
    fetchDashboard();
    // Refresh every 30 seconds
    const interval = setInterval(fetchDashboard, 30000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  const loadAvailableRooms = async (reservation) => {
    try {
      const response = await roomsApi.list(currentProperty.id, { status: 'VACANT' });
      setAvailableRooms(response.data || []);
    } catch (error) {
      toast.error('Failed to load available rooms');
    }
  };

  const handleCheckInClick = async (reservation) => {
    setSelectedReservation(reservation);
    await loadAvailableRooms(reservation);
    setCheckInDialogOpen(true);
  };

  const handleCheckIn = async (roomId) => {
    if (!selectedReservation || !roomId) return;
    
    try {
      await frontOfficeApi.checkIn(selectedReservation.id, currentProperty.id, {
        room_id: roomId,
        id_verified: true,
        create_folio: true
      });
      toast.success(`Guest checked in to room successfully`);
      setCheckInDialogOpen(false);
      setSelectedReservation(null);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-in failed');
    }
  };

  const handleCheckOutClick = (reservation) => {
    setSelectedReservation(reservation);
    setCheckOutDialogOpen(true);
  };

  const handleCheckOut = async () => {
    if (!selectedReservation) return;
    
    try {
      await frontOfficeApi.checkOut(selectedReservation.id, currentProperty.id, {
        folio_settled: true
      });
      toast.success('Guest checked out successfully');
      setCheckOutDialogOpen(false);
      setSelectedReservation(null);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-out failed');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      CONFIRMED: { variant: 'default', icon: CheckCircle, className: 'bg-blue-500' },
      CHECKED_IN: { variant: 'default', icon: DoorOpen, className: 'bg-green-500' },
      CHECKED_OUT: { variant: 'secondary', icon: DoorClosed },
    };
    const config = variants[status] || { variant: 'secondary', icon: Clock };
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="h-3 w-3 mr-1" /> {status?.replace('_', ' ')}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="front-desk-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Front Desk</h1>
          <p className="text-muted-foreground">Manage check-ins, check-outs, and room status for {currentProperty?.name}</p>
        </div>
        <div className="text-right">
          <div className="text-sm text-muted-foreground">Business Date</div>
          <div className="text-lg font-semibold">{dashboard?.business_date || format(new Date(), 'yyyy-MM-dd')}</div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <LogIn className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.arrivals?.pending || 0}</p>
                <p className="text-xs text-muted-foreground">Pending Arrivals</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.arrivals?.completed || 0}</p>
                <p className="text-xs text-muted-foreground">Checked In</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <LogOut className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.departures?.pending || 0}</p>
                <p className="text-xs text-muted-foreground">Departures</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Users className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.inhouse || 0}</p>
                <p className="text-xs text-muted-foreground">In House</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Sparkles className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.rooms?.vacant_clean || 0}</p>
                <p className="text-xs text-muted-foreground">Rooms Ready</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Wrench className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{dashboard?.rooms?.out_of_order || 0}</p>
                <p className="text-xs text-muted-foreground">Out of Order</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="arrivals" data-testid="tab-arrivals">
            <LogIn className="h-4 w-4 mr-2" /> Arrivals ({dashboard?.arrivals?.total || 0})
          </TabsTrigger>
          <TabsTrigger value="departures" data-testid="tab-departures">
            <LogOut className="h-4 w-4 mr-2" /> Departures ({dashboard?.departures?.total || 0})
          </TabsTrigger>
          <TabsTrigger value="inhouse" data-testid="tab-inhouse">
            <Users className="h-4 w-4 mr-2" /> In House ({dashboard?.inhouse || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="arrivals" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Today&apos;s Arrivals</CardTitle>
              <CardDescription>Guests expected to check in today</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
                </div>
              ) : !dashboard?.arrivals?.list?.length ? (
                <div className="text-center py-8 text-muted-foreground" data-testid="no-arrivals">
                  <LogIn className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No arrivals scheduled for today</p>
                </div>
              ) : (
                <Table data-testid="arrivals-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Guest</TableHead>
                      <TableHead>Confirmation</TableHead>
                      <TableHead>Room Type</TableHead>
                      <TableHead>Guests</TableHead>
                      <TableHead>ETA</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboard.arrivals.list.map((res) => (
                      <TableRow key={res.id} data-testid={`arrival-${res.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <User className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                              <div className="font-medium">{res.guest_name}</div>
                              {res.vip_code && <Badge className="bg-amber-500 text-xs">{res.vip_code}</Badge>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{res.confirmation_number}</TableCell>
                        <TableCell>{res.room_type_id}</TableCell>
                        <TableCell>{res.adults}A {res.children > 0 && `+ ${res.children}C`}</TableCell>
                        <TableCell>{res.eta || '-'}</TableCell>
                        <TableCell>{getStatusBadge(res.status)}</TableCell>
                        <TableCell className="text-right">
                          {res.status === 'CONFIRMED' && (
                            <Button 
                              size="sm" 
                              onClick={() => handleCheckInClick(res)}
                              data-testid={`checkin-btn-${res.id}`}
                            >
                              <LogIn className="h-4 w-4 mr-2" /> Check In
                            </Button>
                          )}
                          {res.status === 'CHECKED_IN' && (
                            <Badge variant="secondary">Already Checked In</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="departures" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Today&apos;s Departures</CardTitle>
              <CardDescription>Guests expected to check out today</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
                </div>
              ) : !dashboard?.departures?.list?.length ? (
                <div className="text-center py-8 text-muted-foreground" data-testid="no-departures">
                  <LogOut className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No departures scheduled for today</p>
                </div>
              ) : (
                <Table data-testid="departures-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Guest</TableHead>
                      <TableHead>Room</TableHead>
                      <TableHead>Balance</TableHead>
                      <TableHead>ETD</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboard.departures.list.map((res) => (
                      <TableRow key={res.id} data-testid={`departure-${res.id}`}>
                        <TableCell>
                          <div className="font-medium">{res.guest_name}</div>
                          <div className="text-sm text-muted-foreground font-mono">{res.confirmation_number}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">#{res.rooms?.room_number || 'N/A'}</Badge>
                        </TableCell>
                        <TableCell>
                          {(res.balance_due || 0) > 0 ? (
                            <span className="text-destructive font-medium">ETB {res.balance_due?.toLocaleString()}</span>
                          ) : (
                            <span className="text-green-600">Settled</span>
                          )}
                        </TableCell>
                        <TableCell>{res.etd || '-'}</TableCell>
                        <TableCell>{getStatusBadge(res.status)}</TableCell>
                        <TableCell className="text-right">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleCheckOutClick(res)}
                            data-testid={`checkout-btn-${res.id}`}
                          >
                            <LogOut className="h-4 w-4 mr-2" /> Check Out
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="inhouse" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>In-House Guests</CardTitle>
              <CardDescription>Currently staying guests</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{dashboard?.inhouse || 0} guests currently in house</p>
                <p className="text-sm">View the Reservations page for full in-house list</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Check-In Dialog */}
      <Dialog open={checkInDialogOpen} onOpenChange={setCheckInDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Check In Guest</DialogTitle>
            <DialogDescription>
              Select a room for {selectedReservation?.guest_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedReservation && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Confirmation:</span>
                    <span className="ml-2 font-mono">{selectedReservation.confirmation_number}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Guests:</span>
                    <span className="ml-2">{selectedReservation.adults}A {selectedReservation.children > 0 && `+ ${selectedReservation.children}C`}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Select Room</Label>
                <div className="grid grid-cols-3 gap-2 max-h-[200px] overflow-auto">
                  {availableRooms.length === 0 ? (
                    <div className="col-span-3 text-center py-4 text-muted-foreground">
                      No rooms available
                    </div>
                  ) : (
                    availableRooms.map((room) => (
                      <Button
                        key={room.id}
                        variant="outline"
                        className={cn(
                          "h-auto py-3 flex-col",
                          room.housekeeping_status === 'CLEAN' && "border-green-500"
                        )}
                        onClick={() => handleCheckIn(room.id)}
                        data-testid={`select-room-${room.id}`}
                      >
                        <span className="font-bold">#{room.room_number}</span>
                        <span className="text-xs text-muted-foreground">{room.room_types?.name}</span>
                        {room.housekeeping_status === 'CLEAN' && (
                          <Badge variant="secondary" className="mt-1 text-xs"><Sparkles className="h-3 w-3 mr-1" />Clean</Badge>
                        )}
                      </Button>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Check-Out Dialog */}
      <Dialog open={checkOutDialogOpen} onOpenChange={setCheckOutDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Check Out Guest</DialogTitle>
            <DialogDescription>
              Confirm check-out for {selectedReservation?.guest_name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedReservation && (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Room:</span>
                    <span className="font-medium">#{selectedReservation.rooms?.room_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Balance:</span>
                    <span className={cn(
                      "font-medium",
                      (selectedReservation.balance_due || 0) > 0 ? "text-destructive" : "text-green-600"
                    )}>
                      {(selectedReservation.balance_due || 0) > 0 
                        ? `ETB ${selectedReservation.balance_due?.toLocaleString()} due` 
                        : 'Settled'}
                    </span>
                  </div>
                </div>
              </div>

              {(selectedReservation.balance_due || 0) > 0 && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-amber-800">Outstanding Balance</p>
                    <p className="text-sm text-amber-700">Please settle the folio before checking out.</p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCheckOutDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleCheckOut}
              disabled={(selectedReservation?.balance_due || 0) > 0}
              data-testid="confirm-checkout-btn"
            >
              <LogOut className="h-4 w-4 mr-2" /> Confirm Check Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
